/**
 * Reusable currency + display-unit toggle for list views.
 *
 * Lets the user pick:
 *   - Display currency (INR / USD)
 *   - Exchange rate (free input — INR per 1 USD)
 *   - Display unit (Full / Crores / Millions)
 *
 * State persists in localStorage via the `storageKey` prop so each list view
 * remembers the user's last choice.
 *
 * Usage:
 *   const { selectedCurrency, exchangeRate, displayUnit, convert, format, controlsUI } =
 *       useCurrencyToggle('investorAccounts_currency');
 *   <div>{controlsUI}</div>
 *   <td>{format(amt, sourceCurrency)}</td>
 *
 * The `convert` helper takes (amount, sourceCurrency='INR') and returns the
 * amount expressed in the currently-selected display currency.
 * The `format` helper additionally applies the chosen display unit.
 */
import React, { useEffect, useState, useCallback, useMemo } from 'react';

export function useCurrencyToggle(storageKey = 'listCurrencyToggle') {
  const load = (k, fallback) => {
    try {
      const v = localStorage.getItem(`${storageKey}_${k}`);
      return v == null ? fallback : JSON.parse(v);
    } catch {
      return fallback;
    }
  };
  const save = (k, v) => {
    try {
      localStorage.setItem(`${storageKey}_${k}`, JSON.stringify(v));
    } catch { /* ignore */ }
  };

  const [selectedCurrency, setSelectedCurrency] = useState(() => load('currency', 'INR'));
  const [exchangeRate, setExchangeRate] = useState(() => load('rate', 83));
  const [displayUnit, setDisplayUnit] = useState(() => load('unit', 'full'));

  useEffect(() => { save('currency', selectedCurrency); }, [selectedCurrency]);
  useEffect(() => { save('rate', exchangeRate); }, [exchangeRate]);
  useEffect(() => { save('unit', displayUnit); }, [displayUnit]);

  const convert = useCallback((amount, sourceCurrency = 'INR') => {
    if (amount == null || isNaN(Number(amount))) return 0;
    const src = (sourceCurrency || 'INR').toUpperCase();
    const tgt = selectedCurrency.toUpperCase();
    const a = Number(amount);
    if (src === tgt) return a;
    const r = Number(exchangeRate) || 83;
    if (src === 'INR' && tgt === 'USD') return a / r;
    if (src === 'USD' && tgt === 'INR') return a * r;
    return a;
  }, [selectedCurrency, exchangeRate]);

  /**
   * Format an amount aggregated from per-currency totals.
   * `totalsByCurrency` is the backend payload: { INR: 1000000, USD: 1200 }
   * (or `{ INR: { commitment: 1000000, ... }, USD: { commitment: 1200, ... } }`).
   * If `bucketKey` is provided, we read that sub-key from each currency bucket.
   * Otherwise totals must already be plain numbers.
   */
  const formatFromBuckets = useCallback((totalsByCurrency, bucketKey) => {
    if (!totalsByCurrency || typeof totalsByCurrency !== 'object') return '-';
    let displayTotal = 0;
    let hasAny = false;
    for (const [ccy, bucket] of Object.entries(totalsByCurrency)) {
      let raw;
      if (typeof bucket === 'number') raw = bucket;
      else if (bucket && typeof bucket === 'object') raw = bucketKey ? bucket[bucketKey] : 0;
      else raw = 0;
      if (raw == null || isNaN(Number(raw))) continue;
      hasAny = true;
      displayTotal += convert(Number(raw), ccy);
    }
    if (!hasAny) return '-';
    return formatDisplay(displayTotal, selectedCurrency, displayUnit);
  }, [convert, selectedCurrency, displayUnit]);

  /** Single-currency format: pass the amount in its native currency. */
  const format = useCallback((amount, sourceCurrency = 'INR') => {
    if (amount == null || isNaN(Number(amount)) || Number(amount) === 0) return '-';
    return formatDisplay(convert(amount, sourceCurrency), selectedCurrency, displayUnit);
  }, [convert, selectedCurrency, displayUnit]);

  const controlsUI = useMemo(() => (
    <div className="inline-flex items-center gap-2 text-xs flex-wrap" data-testid="currency-toggle-controls">
      <div className="inline-flex items-center bg-gray-100 rounded-md p-0.5">
        {['INR', 'USD'].map(c => (
          <button
            key={c}
            type="button"
            onClick={() => setSelectedCurrency(c)}
            className={`px-2.5 py-1 rounded ${selectedCurrency === c ? 'bg-white shadow text-blue-600 font-medium' : 'text-gray-500 hover:text-gray-700'}`}
            data-testid={`currency-toggle-${c.toLowerCase()}`}
          >
            {c === 'INR' ? '\u20B9 INR' : '$ USD'}
          </button>
        ))}
      </div>
      {selectedCurrency === 'USD' ? (
        <div className="inline-flex items-center gap-1 text-gray-500">
          <span>Rate (₹/$):</span>
          <input
            type="number"
            min="0"
            step="0.01"
            value={exchangeRate}
            onChange={(e) => setExchangeRate(Number(e.target.value) || 0)}
            className="w-16 px-1.5 py-0.5 border rounded text-right font-mono"
            data-testid="currency-exchange-rate-input"
          />
        </div>
      ) : null}
      <div className="inline-flex items-center bg-gray-100 rounded-md p-0.5">
        {[
          { v: 'full', l: 'Full' },
          { v: 'millions', l: 'M' },
          { v: 'crores', l: 'Cr' },
        ].map(o => (
          <button
            key={o.v}
            type="button"
            onClick={() => setDisplayUnit(o.v)}
            className={`px-2 py-1 rounded ${displayUnit === o.v ? 'bg-white shadow text-blue-600 font-medium' : 'text-gray-500 hover:text-gray-700'}`}
            data-testid={`display-unit-${o.v}`}
            title={o.v === 'crores' ? 'Crores (1,00,00,000)' : o.v === 'millions' ? 'Millions' : 'Full amount'}
          >
            {o.l}
          </button>
        ))}
      </div>
    </div>
  ), [selectedCurrency, exchangeRate, displayUnit]);

  return {
    selectedCurrency,
    exchangeRate,
    displayUnit,
    convert,
    format,
    formatFromBuckets,
    setSelectedCurrency,
    setExchangeRate,
    setDisplayUnit,
    controlsUI,
  };
}


function formatDisplay(displayAmount, currency, unit) {
  if (displayAmount == null) return '-';
  const sym = currency === 'INR' ? '\u20B9' : '$';
  const locale = currency === 'INR' ? 'en-IN' : 'en-US';
  if (unit === 'crores') {
    return `${sym}${(displayAmount / 10000000).toFixed(2)} Cr`;
  }
  if (unit === 'millions') {
    return `${sym}${(displayAmount / 1000000).toFixed(2)} M`;
  }
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(displayAmount);
}

export default useCurrencyToggle;
